import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Grid,
  TextField,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { AlertMessagesContext } from 'react-alert-messages';
import { InventoryContext } from 'Context/InventoryContext';
import StockReportService from 'services/StockReportService';
import { WarehouseContext } from 'Context/WarehouseContext';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import { roundOff } from 'helpers/NumberFormatHelper';
import { ITEM_TYPES } from 'const';

export default function CreateStockConsumptionDialog({ open, handleClose, handleSuccess }) {
  const { t: tCommon } = useTranslation('common');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { products, getProducts } = useContext(InventoryContext);

  const { shop } = useContext(ShopContext);
  const { warehouses, getWarehouses, warehouseProducts, getWarehouseProducts } = useContext(WarehouseContext);

  const [consumptionItems, setConsumptionItems] = useState([]);
  const [savingConsumption, setSavingConsumption] = useState(false);

  const [tempSelectedItem, setTempSelectedItem] = useState(null);
  const [tempNewStock, setTempNewStock] = useState('');

  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  const isWarehouseActive = ShopsHelper.isWarehouseFeatureActive(shop);

  const availableProducts = React.useMemo(() => {
    let list = (products || []).filter((p) => p.type === ITEM_TYPES.INVENTORY);
    if (isWarehouseActive && selectedWarehouseId) {
      const warehouseItemIds = new Set(
        (warehouseProducts || [])
          .filter((wp) => String(wp.warehouse?.id || wp.warehouse) === String(selectedWarehouseId))
          .map((wp) => String(wp.item?.id || wp.item))
      );
      list = list.filter((p) => warehouseItemIds.has(String(p.id)));
    }
    return list;
  }, [products, warehouseProducts, selectedWarehouseId, isWarehouseActive]);

  const getProductStock = (itemObj) => {
    if (!itemObj) return 0;
    if (isWarehouseActive && selectedWarehouseId) {
      const wp = warehouseProducts.find(
        (p) => String(p.warehouse?.id || p.warehouse) === String(selectedWarehouseId) && String(p.item?.id || p.item) === String(itemObj.id)
      );
      return wp ? wp.stock : 0;
    }
    return itemObj.stock || 0;
  };

  useEffect(() => {
    if (open) {
      if (isWarehouseActive) {
        getWarehouses({ useCache: true });
        getWarehouseProducts({ useCache: true });
        if (shop?.config?.default_warehouse) {
          setSelectedWarehouseId(String(shop.config.default_warehouse));
        } else {
          setSelectedWarehouseId('');
        }
      } else {
        setSelectedWarehouseId('');
      }
    }
  }, [open, isWarehouseActive, getWarehouses, getWarehouseProducts, shop]);

  useEffect(() => {
    if (open) {
      if (!products) {
        getProducts();
      }
    }
  }, [open, products, getProducts]);

  useEffect(() => {
    setTempNewStock('');
  }, [tempSelectedItem]);

  useEffect(() => {
    if (!open) {
      setConsumptionItems([]);
      setTempSelectedItem(null);
      setTempNewStock('');
      setSelectedWarehouseId('');
    }
  }, [open]);

  if (!open) return null;

  const handleStockChange = (index, value) => {
    const next = [...consumptionItems];
    next[index].newStock = value;
    setConsumptionItems(next);
  };

  const handleDeleteItem = (index) => {
    const next = [...consumptionItems];
    next.splice(index, 1);
    setConsumptionItems(next);
  };

  const handleAddNewItemToList = () => {
    if (isWarehouseActive && !selectedWarehouseId) {
      postAlertMessage({ text: tCommon('Please select a warehouse first'), type: 'failed' });
      return;
    }
    if (!tempSelectedItem) return;
    const exists = consumptionItems.some((itm) => itm.item.id === tempSelectedItem.id);
    if (exists) {
      postAlertMessage({ text: tCommon('Item already added'), type: 'failed' });
      return;
    }
    if (tempNewStock === '' || isNaN(Number(tempNewStock)) || Number(tempNewStock) < 0) {
      postAlertMessage({
        text: tCommon('Please enter a valid stock quantity'),
        type: 'failed',
      });
      return;
    }

    setConsumptionItems([
      ...consumptionItems,
      {
        item: tempSelectedItem,
        newStock: tempNewStock,
      },
    ]);

    setTempSelectedItem(null);
    setTempNewStock('');
  };

  const handleSaveConsumption = async () => {
    if (isWarehouseActive && !selectedWarehouseId) {
      postAlertMessage({ text: tCommon('Please select a warehouse first'), type: 'failed' });
      return;
    }
    if (tempSelectedItem) {
      postAlertMessage({
        text: tCommon('You forgot to add the selected item to the list. Click Add first or clear the search.'),
        type: 'failed',
      });
      return;
    }
    if (consumptionItems.length === 0) {
      postAlertMessage({ text: tCommon('Please add at least one item'), type: 'failed' });
      return;
    }
    const invalidItem = consumptionItems.find(
      (itm) => isNaN(Number(itm.newStock)) || Number(itm.newStock) < 0 || itm.newStock === ''
    );
    if (invalidItem) {
      postAlertMessage({
        text: tCommon('Please enter a valid stock quantity for all items'),
        type: 'failed',
      });
      return;
    }

    setSavingConsumption(true);
    try {
      const payload = consumptionItems.map((itm) => ({
        id: itm.item.id,
        stock: Number(itm.newStock),
      }));

      await StockReportService.createStockConsumption(payload, selectedWarehouseId);
      postAlertMessage({ text: tCommon('Consumption created successfully'), type: 'success' });
      setConsumptionItems([]);
      getProducts({ useCache: false });
      if (isWarehouseActive && selectedWarehouseId) {
        getWarehouseProducts({ useCache: false });
      }
      handleSuccess();
      handleClose();
    } catch (err) {
      postAlertMessage({ text: err.message || 'Failed to create consumption', type: 'failed' });
    } finally {
      setSavingConsumption(false);
    }
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper} style={{ width: '60%', maxWidth: '800px' }}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Create Stock Consumption')}</h2>
          <IconButton size="medium" onClick={handleClose} disabled={savingConsumption}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.inputSec} style={{ padding: '24px' }}>
          <Grid container spacing={3}>
            {isWarehouseActive && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={tCommon('Select Warehouse')}
                  value={selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                >
                  <MenuItem value="">-- {tCommon('Select Warehouse')} --</MenuItem>
                  {warehouses.map((wh) => (
                    <MenuItem key={wh.id} value={String(wh.id)}>
                      {wh.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: '600' }}>{tCommon('PLU')}</TableCell>
                      <TableCell style={{ fontWeight: '600' }}>{tCommon('Item Name')}</TableCell>
                      <TableCell style={{ fontWeight: '600', width: '130px' }}>{tCommon('Current Stock')}</TableCell>
                      <TableCell style={{ fontWeight: '600', width: '130px' }}>{tCommon('New Stock')}</TableCell>
                      <TableCell style={{ fontWeight: '600', width: '130px' }}>{tCommon('Difference')}</TableCell>
                      <TableCell style={{ fontWeight: '600', width: '80px' }} align="center">
                        {tCommon('Action')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow style={{ backgroundColor: '#fcfcfd' }}>
                      <TableCell colSpan={2}>
                        <Autocomplete
                          value={tempSelectedItem}
                          onChange={(event, newValue) => {
                            setTempSelectedItem(newValue);
                          }}
                          options={availableProducts}
                          getOptionLabel={(option) => (option ? `${option.name} (${option.plu})` : '')}
                          isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                          filterOptions={(options, { inputValue }) => {
                            const query = inputValue.toLowerCase();
                            return options.filter(
                              (option) =>
                                option.name.toLowerCase().includes(query) ||
                                String(option.plu).includes(query)
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={tCommon('Search Product by Name or PLU')}
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>{tempSelectedItem ? getProductStock(tempSelectedItem) : '-'}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={tempNewStock}
                          onChange={(e) => setTempNewStock(e.target.value)}
                          disabled={!tempSelectedItem}
                          inputProps={{ min: 0, style: { padding: '6px 10px' } }}
                          variant="outlined"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          if (!tempSelectedItem || tempNewStock === '' || isNaN(Number(tempNewStock))) {
                            return '-';
                          }
                          const currentStock = getProductStock(tempSelectedItem);
                          const diff = Number(tempNewStock) - currentStock;
                          const formattedDiff = roundOff(diff, 3);
                          const color = diff > 0 ? '#00a65a' : (diff < 0 ? '#E53935' : 'inherit');
                          return (
                            <span style={{ color, fontWeight: '600' }}>
                              {diff > 0 ? `+${formattedDiff}` : formattedDiff}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleAddNewItemToList}
                          disabled={!tempSelectedItem}
                          style={{ minWidth: '60px', textTransform: 'none' }}
                        >
                          {tCommon('Add')}
                        </Button>
                      </TableCell>
                    </TableRow>

                    {consumptionItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" style={{ color: '#9ca3af', padding: '24px' }}>
                          {tCommon('No items added. Search and select items above.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      consumptionItems.map((row, index) => (
                        <TableRow key={row.item.id}>
                          <TableCell>{row.item.plu}</TableCell>
                          <TableCell>{row.item.name}</TableCell>
                          <TableCell>{getProductStock(row.item)}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={row.newStock}
                              onChange={(e) => handleStockChange(index, e.target.value)}
                              inputProps={{ min: 0, style: { padding: '6px 10px' } }}
                              variant="outlined"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              if (row.newStock === '' || isNaN(Number(row.newStock))) {
                                return '-';
                              }
                              const currentStock = getProductStock(row.item);
                              const diff = Number(row.newStock) - currentStock;
                              const formattedDiff = roundOff(diff, 3);
                              const color = diff > 0 ? '#00a65a' : (diff < 0 ? '#E53935' : 'inherit');
                              return (
                                <span style={{ color, fontWeight: '600' }}>
                                  {diff > 0 ? `+${formattedDiff}` : formattedDiff}
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteItem(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button
              onClick={handleClose}
              variant="contained"
              color="secondary"
              className={styles.closeBtn}
              disabled={savingConsumption}
            >
              {tCommon('Cancel')}
            </Button>
            <Button
              onClick={handleSaveConsumption}
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={savingConsumption || (consumptionItems.length === 0 && !tempSelectedItem)}
            >
              {savingConsumption ? <CircularProgress size={20} color="inherit" /> : tCommon('Save')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
